import { API_BASE_URL } from './Constants';
import { authenticatedFetch } from './ApiInterceptor';

/**
 * Fetches tournaments with optimized data loading
 * @param {Object} options - Query options
 * @param {boolean} options.excludeCompleted - Whether to exclude completed tournaments (default: true)
 * @param {boolean} options.onlyCompleted - Whether to fetch only completed tournaments
 * @returns {Promise<Array>} Array of tournament objects
 */
export const fetchTournaments = async (options = {}) => {
  try {
    const excludeCompleted = options.excludeCompleted !== false;
    const onlyCompleted = options.onlyCompleted === true;
    let queryParams = '';

    if (onlyCompleted) {
      queryParams = '?only_completed=true';
    } else if (excludeCompleted) {
      queryParams = '?exclude_completed=true';
    }

    const response = await authenticatedFetch(`/tournamentsapi${queryParams}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch tournaments: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    //console.log("Raw tournaments data:", data); // Add logging to check API response
    
    // Map new field names to match the updated response structure
    return data.map(tournament => ({
      id: tournament.id,
      name: tournament.name,
      game: tournament.game,
      tournamentType: tournament.tournament_type,
      registrationStartDate: tournament.registration_start_date,
      registrationEndDate: tournament.registration_end_date,
      tentativeHappeningDate: tournament.tentative_happening_date,
      host: tournament.host,
      prizeCoin: tournament.prize_coin,
      prizeMoney: tournament.prize_money,
      prizeGoodies: tournament.prize_goodies,
      entryFees: tournament.entry_fees,
      entryFeeType: tournament.entry_fee_type,
      tournamentStatus: tournament.tournament_status,
      maxSlots: tournament.max_slots,
      winnerPlayers: tournament.winner_players,
      maxWinnerPlayers: tournament.max_winner_players,
      tournamentFormat: tournament.tournament_format,
      isRegistered: tournament.is_registered, // Make sure this mapping exists
      is_registered: tournament.is_registered, // Keep the original property too
      totalPlayersRegistered: tournament.total_players_registered,
      totalTeamsRegistered: tournament.total_teams_registered, // Add this line to map team registrations
      rewardsClaimed: tournament.rewards_claimed,
    }));
  } catch (error) {
    // console.error("Error fetching tournaments:", error);
    throw error;
  }
};

/**
 * Fetches details for a specific tournament
 * @param {string} tournamentId - The ID of the tournament
 * @returns {Promise<Object>} Tournament details
 */
export const fetchTournamentDetails = async (tournamentId) => {
  try {
    const response = await authenticatedFetch(`/tournamentsapi/${tournamentId}/`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch tournament details: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    // // console.log(`Tournament ${tournamentId} details:`, data);
    return data;
  } catch (error) {
    // console.error(`Error fetching tournament ${tournamentId} details:`, error);
    throw error;
  }
};

/**
 * Registers a player for a solo tournament
 * @param {string} tournamentId - The ID of the tournament
 * @param {Object} payload - Registration data including game_id and game_username
 * @returns {Promise<Object>} Registration confirmation
 */
export const registerForTournament = async (tournamentId, payload) => {
  try {
    // Check if already registered first
    const tournamentDetails = await fetchTournamentDetails(tournamentId);
    if (tournamentDetails.is_registered) {
      throw new Error("You're already registered for this tournament");
    }
    
    const response = await authenticatedFetch(`/tournamentsapi/${tournamentId}/register-solo/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorResponse = await response.json().catch(() => null);
      
      // Check if it's a structured error with specific error information
      if (errorResponse && errorResponse.error) {
        // Create a structured error object with additional information
        const structuredError = new Error(errorResponse.error);
        structuredError.statusCode = response.status;
        structuredError.details = errorResponse;
        throw structuredError;
      }
      
      // Fallback to generic error
      throw new Error(`Failed to register for tournament: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // console.error("Error registering for tournament:", error);
    throw error;
  }
};

/**
 * Registers a team for a team tournament (squad/duo)
 * @param {string} tournamentId - The ID of the tournament
 * @param {Object} payload - Registration data including team_id and optionally member_game_ids/usernames
 * @returns {Promise<Object>} Registration confirmation
 */
export const registerTeamForTournament = async (tournamentId, payload) => {
  try {
    // Check if already registered first
    const tournamentDetails = await fetchTournamentDetails(tournamentId);
    if (tournamentDetails.is_registered) {
      throw new Error("Your team is already registered for this tournament");
    }
    
    const response = await authenticatedFetch(`/tournamentsapi/${tournamentId}/register-team/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorResponse = await response.json().catch(() => null);
      
      // Handle specific error for missing game IDs
      if (errorResponse && errorResponse.code === "E003") {
        const structuredError = new Error(errorResponse.error);
        structuredError.statusCode = response.status;
        structuredError.details = errorResponse;
        structuredError.missingPlayerIds = errorResponse.missing_player_ids;
        throw structuredError;
      }
      
      // Handle other structured errors
      if (errorResponse && errorResponse.error) {
        const structuredError = new Error(errorResponse.error);
        structuredError.statusCode = response.status;
        structuredError.details = errorResponse;
        throw structuredError;
      }
      
      // Fallback to generic error
      throw new Error(`Failed to register team for tournament: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // console.error("Error registering team for tournament:", error);
    throw error;
  }
};

/**
 * Fetches tournament stages and matches
 * @param {string} tournamentId - The ID of the tournament
 * @returns {Promise<Object>} Tournament stages and matches
 */

// Example response format for fetchTournamentStages:
/*
[
  {
    "id": 3,
    "stage_name": "Semi-final  ffbol",
    "start_date": "2026-06-12",
    "end_date": "2026-06-13",
    "max_teams_to_play": 96,
    "qualified_teams": [],
    "total_stage_matches": 2,
    "teams_per_match": 48,
    "teams_to_qualify_per_match": 24,
    "match_dates": [
      {
        "match_id": 4,
        "match_date": "2026-06-12",
        "match_time": "19:00:00",
        "match_status": "scheduled"
      }
    ]
  },
  {
    "id": 4,
    "stage_name": "final ffbol",
    "start_date": "2026-06-14",
    "end_date": "2026-06-15",
    "max_teams_to_play": 48,
    "qualified_teams": [],
    "total_stage_matches": 1,
    "teams_per_match": 48,
    "teams_to_qualify_per_match": 1,
    "match_dates": [
      {
        "match_id": 5,
        "match_date": "2026-06-14",
        "match_time": "19:00:00",
        "match_status": "scheduled"
      }
    ]
  }
]
*/
export const fetchTournamentStages = async (tournamentId) => {
  try {
    // // console.log("fetchTournamentStages called with ID:", tournamentId);
    const url = `/tournamentsapi/${tournamentId}/stages/`;
    // // console.log("Attempting to fetch from URL:", url);
    
    const response = await authenticatedFetch(url);
    // // console.log("Stage API response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      // console.error("Stage API error response:", errorText);
      throw new Error(`Failed to fetch tournament stages: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    // // console.log(`Tournament ${tournamentId} stages data:`, data);
    return data;
  } catch (error) {
    // console.error(`Error in fetchTournamentStages for tournament ${tournamentId}:`, error);
    throw error;
  }
};

/**
 * Fetches tournaments the user is registered for
 * @returns {Promise<Array>} Array of tournament objects
 */
export const fetchMyTournaments = async () => {
  try {
    const response = await authenticatedFetch('/playerapi/my-tournaments/');

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch my tournaments: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    // //console.log("Fetched my tournaments:", data);
    return data;
  } catch (error) {
    // console.error("Error fetching my tournaments:", error);
    throw error;
  }
};

/**
 * Fetches participants for a specific tournament
 * @param {string} tournamentId - The ID of the tournament
 * @returns {Promise<Object>} Tournament participants data
 */
export const fetchTournamentParticipants = async (tournamentId) => {
  try {
    const response = await authenticatedFetch(`/tournamentsapi/${tournamentId}/participants/`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch tournament participants: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // console.error(`Error fetching tournament ${tournamentId} participants:`, error);
    throw error;
  }
};

/**
 * Fetches match details for a specific tournament
 * @param {string} tournamentId - The ID of the tournament
 * @returns {Promise<Array>} Array of match objects
 */
export const fetchTournamentMatches = async (tournamentId) => {
  try {
    const response = await authenticatedFetch(`/tournamentsapi/${tournamentId}/matches/`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch tournament matches: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // console.error(`Error fetching tournament ${tournamentId} matches:`, error);
    throw error;
  }
};

/**
 * Registers a player for a solo tournament (duplicate function)
 * @param {string} tournamentId - The ID of the tournament
 * @param {Object} payload - Registration data including game_id and game_username
 * @returns {Promise<Object>} Registration confirmation
 */
export const registerSoloTournament = async (tournamentId, payload) => {
  try {
    const response = await authenticatedFetch(`/tournamentsapi/${tournamentId}/register-solo/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to register for solo tournament: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // console.error(`Error registering for solo tournament ${tournamentId}:`, error);
    throw error;
  }
};
